/**
 * Image Compression Utility
 *
 * Automatski komprimira slike prije uploada za optimalan prikaz na mobilnim uređajima.
 *
 * Postavke:
 * - Max širina: 1200px (dovoljno za retina display)
 * - Max visina: 1200px
 * - Kvaliteta: 80% JPEG
 * - Rezultat: 80-150 KB umjesto 3-5 MB
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  outputFormat?: 'jpeg' | 'webp';
}

export interface CompressionResult {
  blob: Blob;
  width: number;
  height: number;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1200,
  maxHeight: 1200,
  quality: 0.8,
  outputFormat: 'jpeg',
};

/**
 * Komprimira sliku za optimalan prikaz na mobilnim uređajima
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const originalSize = file.size;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      // Izračunaj nove dimenzije zadržavajući aspect ratio
      let { width, height } = img;

      if (width > opts.maxWidth) {
        height = (height * opts.maxWidth) / width;
        width = opts.maxWidth;
      }

      if (height > opts.maxHeight) {
        width = (width * opts.maxHeight) / height;
        height = opts.maxHeight;
      }

      // Postavi canvas dimenzije
      canvas.width = width;
      canvas.height = height;

      // Nacrtaj sliku na canvas (automatski resize)
      ctx.drawImage(img, 0, 0, width, height);

      // Konvertiraj u blob
      const mimeType = opts.outputFormat === 'webp' ? 'image/webp' : 'image/jpeg';

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          resolve({
            blob,
            width: Math.round(width),
            height: Math.round(height),
            originalSize,
            compressedSize: blob.size,
            compressionRatio: Number(((1 - blob.size / originalSize) * 100).toFixed(1)),
          });
        },
        mimeType,
        opts.quality
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Učitaj sliku
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Komprimira sliku i vraća File objekt (za kompatibilnost s uploadom)
 */
export async function compressImageToFile(
  file: File,
  options: CompressionOptions = {}
): Promise<{ file: File; result: CompressionResult }> {
  const result = await compressImage(file, options);

  // Kreiraj novi File objekt iz Blob-a
  const extension = options.outputFormat === 'webp' ? 'webp' : 'jpg';
  const originalName = file.name.replace(/\.[^/.]+$/, '');
  const newFileName = `${originalName}.${extension}`;

  const compressedFile = new File([result.blob], newFileName, {
    type: result.blob.type,
    lastModified: Date.now(),
  });

  return { file: compressedFile, result };
}

/**
 * Provjeri treba li slika kompresiju
 */
export function needsCompression(file: File, maxSizeKB: number = 200): boolean {
  const fileSizeKB = file.size / 1024;
  return fileSizeKB > maxSizeKB;
}

/**
 * Formatiraj veličinu datoteke za prikaz
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Presets za različite namjene
 *
 * TRENUTNO: Optimizirano za Supabase Free tier (1 GB storage)
 *
 * KAD PREĐEŠ NA PRO ($25/mj), promijeni 'gallery' na:
 * - maxWidth: 1600
 * - maxHeight: 1600
 * - quality: 0.85
 * Rezultat: ~200 KB/slika umjesto ~120 KB
 */
export const COMPRESSION_PRESETS = {
  // Za glavne slike galerije
  // FREE TIER: 1200px, 80% (~120 KB)
  // PRO TIER: promijeni na 1600px, 85% (~200 KB)
  gallery: {
    maxWidth: 1200,
    maxHeight: 1200,
    quality: 0.8,
    outputFormat: 'jpeg' as const,
  },

  // Za thumbnail slike (400px, srednja kvaliteta)
  thumbnail: {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.7,
    outputFormat: 'jpeg' as const,
  },

  // Za cover slike (1200x600, visoka kvaliteta)
  cover: {
    maxWidth: 1200,
    maxHeight: 600,
    quality: 0.85,
    outputFormat: 'jpeg' as const,
  },

  // Za fullscreen/lightbox (1920px, najviša kvaliteta)
  fullscreen: {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.9,
    outputFormat: 'jpeg' as const,
  },
};

export default compressImage;
